import { H4, Modal, Text } from '@payaca/ambrosia';
import { Fragment, type FC, type ReactNode, useState } from 'react';
import AttributionText from './AttributionText';

const GOOGLE_TERMS =
  '[Terms](https://maps.google.com/help/terms_maps/) · [Privacy](https://policies.google.com/privacy)';
const OSM_COPYRIGHT_URL = 'https://www.openstreetmap.org/copyright';

type MapAttributionProps = {
  googleData: 'imagery' | 'imageryAndSolarData' | null;
  showsBuildingOutlines: boolean;
  titleBoundaryAttribution: string | null;
};

const MapAttribution: FC<MapAttributionProps> = (props) => {
  const { googleData, showsBuildingOutlines, titleBoundaryAttribution } = props;
  const [isOpen, setIsOpen] = useState(false);

  const sources = [
    googleData
      ? {
          heading:
            googleData === 'imagery' ? 'Imagery' : 'Imagery and solar data',
          markdown:
            googleData === 'imagery'
              ? `Copyright Google. ${GOOGLE_TERMS}`
              : `Includes solar data from Google. Copyright Google. ${GOOGLE_TERMS}`,
        }
      : null,
    showsBuildingOutlines
      ? {
          heading: 'Building outlines',
          markdown: `Copyright [OpenStreetMap](${OSM_COPYRIGHT_URL}) contributors, available under the Open Database License.`,
        }
      : null,
    titleBoundaryAttribution
      ? { heading: 'Property boundary', markdown: titleBoundaryAttribution }
      : null,
  ].filter((source) => source !== null);

  if (sources.length === 0) return null;

  const credits: { key: string; content: ReactNode }[] = [
    ...(googleData === 'imageryAndSolarData'
      ? [{ key: 'google', content: 'Source: Includes solar data from Google' }]
      : []),
    ...(showsBuildingOutlines
      ? [
          {
            key: 'osm',
            content: (
              <>
                Building outlines ©{' '}
                <a
                  href={OSM_COPYRIGHT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  OpenStreetMap
                </a>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="absolute bottom-5 right-2 rounded bg-white/70 px-2 py-0.5 text-center text-sm text-blue-900">
        {credits.map((credit) => (
          <Fragment key={credit.key}>
            <span className="block md:inline">{credit.content}</span>
            <span aria-hidden className="mx-2 hidden md:inline">
              |
            </span>
          </Fragment>
        ))}
        <button
          type="button"
          className="mx-auto block bg-transparent underline md:mx-0 md:inline"
          onClick={() => setIsOpen(true)}
        >
          {credits.length > 0
            ? 'Further licensing & attribution'
            : 'Licensing & attribution'}
        </button>
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Licensing and attribution"
      >
        <Modal.Body>
          <div className="space-y-4">
            {sources.map((source) => (
              <section key={source.heading} className="space-y-1">
                <H4>{source.heading}</H4>
                <Text>
                  <AttributionText markdown={source.markdown} />
                </Text>
              </section>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MapAttribution;
